// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { hasEnoughInventory as hasEnoughInventorySelector } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductGridContainer, { ProductGridContainerStyle } from "@sc/App/Components/Product/ProductGridTemplate";
import ProductPriceAndActions, { ProductPriceAndActionsStyle } from "@sc/App/Components/Product/ProductPriceAndActions";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@sc/Overrides/Components/ProductPartNumbers";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    recommendationId?: number;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    // XNG-Change
    menuItemView?: boolean;
    showAddToCartIcon?: boolean;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    // XNG-CHANGE
    itemListName?: string;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    imageInfoWrapper?: InjectableCss;
    infoSectionWrapper?: InjectableCss;
    priceAndActionsWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceAndActionsStyle?: ProductPriceAndActionsStyle;
    productGridContainer?: ProductGridContainerStyle;
    productBadgesStyles?: {
        image?: ProductImageBadgesStyles;
        text?: ProductTextBadgesStyles;
    };
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    recommendationId,
    sendProductRecommendationTracking,
    menuItemView = false,
    actionsTemplate,
    // XNG-CHANGE
    itemListName,
}: Props) => {
    // XNG-Change
    const [styles] = React.useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges: false, showImageBadges });

    const hasEnoughInventory = useAppSelector(state => hasEnoughInventorySelector(state, productContext));
    const emptyActionSection = !hasEnoughInventory && menuItemView;

    const productDetailPath = productContext.productInfo.productDetailPath;

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridContainer
                    extendedStyles={styles.productGridContainer}
                    id={`product_${product.id}`}
                    data-test-selector="productContainer"
                >
                    <StyledWrapper {...styles.imageInfoWrapper} className="iamge-info-wrapper">
                        <ProductGridContainer.Image>
                            {showImage && (
                                <ProductImage
                                    product={productContext}
                                    onClick={() => trackProductRecommendation(true)}
                                    extendedStyles={styles.productImageStyles}
                                />
                            )}
                            {/* {showImage && (
                                <StyledWrapper {...styles.badgeAreaWrapper}    
                                ref={badgeAreaWrapperRef}
                                data-test-selector="productCarouselBadge">
                                    <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                                    <OverlayBadges
                                        badges={overlayBadges}
                                        wrapperCss={overlayBadgesWrapperStyles}
                                        extendedStyles={styles.overlayBadgeStyles}
                                        maxLength={1}
                                    />
                                </StyledWrapper>
                            )} */}
                        </ProductGridContainer.Image>
                        <ProductGridContainer.Info>
                            <StyledWrapper {...styles.infoSectionWrapper}>
                                {showBrand && product.brand && (
                                    <StyledWrapper {...styles.productBrandWrapper}>
                                        <ProductBrand
                                            brand={product.brand}
                                            extendedStyles={styles.productBrandStyles}
                                            {...{ productDetailPagePath: productDetailPath }}
                                        />
                                    </StyledWrapper>
                                )}
                                {showTitle && (
                                    <ProductDescription
                                        product={productContext}
                                        onClick={() => trackProductRecommendation(true)}
                                        extendedStyles={styles.productDescriptionStyles}
                                    />
                                )}
                                {showPartNumbers && (
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        extendedStyles={styles.productPartNumbersStyles}
                                        {...{
                                            customerPartNumber: product.properties.customerPartNumber,
                                            isPLPGridView: true,
                                            distributorNumber: product.properties.distributorNumber,
                                            displayManufacturerItemForB2b: true,
                                        }}
                                    />
                                )}
                                {/* {menuItemView && canSeePrices && (
                                    <ProductPrice product={productContext} extendedStyles={styles.productPriceStyles} />
                                )} */}
                                {/* {menuItemView && (
                                    <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                                )} */}
                            </StyledWrapper>
                        </ProductGridContainer.Info>
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceAndActionsWrapper} className="price-an-actions-wrapper">
                        {!emptyActionSection && (
                            <ProductPriceAndActions
                                showAvailability={true}
                                showCompare={false}
                                showAddToList={showAddToList}
                                showPrice={showPrice}
                                showOriginPrice={false}
                                showTextBadges={showTextBadges}
                                showImageBadges={showImageBadges}
                                maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                                maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                                showAddToCartActions={showAddToCart}
                                extendedStyles={styles.productPriceAndActionsStyles}
                                containerClassName="pricing-info-container"
                                // XNG-CHANGE
                                itemListName={itemListName}
                            />
                        )}
                    </StyledWrapper>
                    {actionsTemplate ? actionsTemplate(productContext.product) : null}
                </ProductGridContainer>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
